/*
 * Pure, DOM-free logic for the cart drawer (delivery method + date picker).
 *
 * Loaded in the theme as a browser global (window.CartDrawerLogic) before
 * custom.js, and imported directly by the Node test suite. Keep this file
 * free of DOM, fetch, and Shopify globals so it stays unit-testable — all
 * inputs (settings, method, "now") are passed in explicitly.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.CartDrawerLogic = factory();
    // RØNHAVE-FAST-DATO (browser only): cart-drawer.liquid marks the fields
    // with data-fixed-date / data-fixed-times when the cart holds a product
    // with a fixed pickup day. Every read of window.__cartDrawerSettings
    // (custom.js cfg()) gets those merged in, so the date picker and time
    // select follow the fixed day without custom.js having to know about it.
    if (typeof document !== 'undefined') {
      var baseSettings = root.__cartDrawerSettings || {};
      try {
        Object.defineProperty(root, '__cartDrawerSettings', {
          configurable: true,
          get: function () {
            var extra = {};
            // RØNHAVE-LOKAL: postcodes/days for local delivery, rendered by Liquid.
            var m = document.querySelector('.cart-drawer__methods[data-local-postcodes]');
            if (m) {
              extra.localPostcodes = m.getAttribute('data-local-postcodes') || '';
              extra.localLabel = m.getAttribute('data-local-label') || '';
              var ld = m.getAttribute('data-local-days');
              if (ld) extra.localDays = ld.split(',').map(function (s) { return parseInt(s, 10); }).filter(function (n) { return n >= 0 && n <= 6; });
            }
            var f = document.querySelector('.cart-drawer__fields[data-fixed-date]');
            if (f) {
              extra.fixedDate = f.getAttribute('data-fixed-date') || '';
              extra.fixedTimes = f.getAttribute('data-fixed-times') || '';
            }
            return Object.keys(extra).length ? Object.assign({}, baseSettings, extra) : baseSettings;
          },
          set: function (v) { baseSettings = v || {}; },
        });
      } catch (e) { /* keep the plain settings object */ }
    }
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Fallback delivery days (Wed/Thu/Fri) used only when a method has no days
  // configured in the theme settings.
  const DEFAULT_DAYS = [3, 4, 5];

  function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function isoDate(d) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  // Selectable weekday numbers (0=Sun..6=Sat) for the given method.
  // Local delivery uses its own days when set, otherwise the shop's pickup days.
  function deliveryDaysFor(method, settings) {
    let set = settings.homeDays;
    if (method === 'pickup') set = settings.pickupDays;
    if (method === 'local') {
      set = Array.isArray(settings.localDays) && settings.localDays.length
        ? settings.localDays
        : settings.pickupDays;
    }
    return Array.isArray(set) && set.length ? set : DEFAULT_DAYS;
  }

  // Human label stored on the cart / order for the given method.
  function methodLabelFor(method, settings) {
    const home = settings.homeLabel || 'Levering til hjemmeadresse';
    const pickup = settings.pickupLabel || 'Afhentning i butik';
    const local = settings.localLabel || 'Lokal levering';
    if (method === 'pickup') return pickup;
    if (method === 'local') return local;
    return home;
  }

  // RØNHAVE-LOKAL: whether a postcode is inside the local delivery area.
  function isLocalPostcode(code, settings) {
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 4) return false;
    return parseCsv(settings.localPostcodes).includes(clean);
  }

  // Splits a comma-separated setting into trimmed, non-empty values.
  function parseCsv(value) {
    return String(value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // RØNHAVE-FAST-DATO: When the cart holds a product with a fixed pickup day
  // (e.g. the New Year's menu), Liquid passes fixedDate (ISO) and fixedTimes
  // (CSV). That one date — and only those times — overrides the normal
  // weekday/lead-time/blocked-date rules.
  function fixedDateOf(settings) {
    return settings && settings.fixedDate ? String(settings.fixedDate) : '';
  }

  // Pickup times offered on a given date. Saturday and Sunday can carry their
  // own lists; a blank list for either falls back to the weekday list, so a
  // merchant only has to fill in the days that actually differ.
  function pickupTimesFor(date, settings) {
    if (fixedDateOf(settings)) {
      const fixed = parseCsv(settings.fixedTimes);
      if (fixed.length) return fixed;
    }
    const day = date.getDay();
    const raw =
      day === 6 ? settings.pickupTimesSat :
      day === 0 ? settings.pickupTimesSun :
      settings.pickupTimesWeekday;
    const times = parseCsv(raw);
    return times.length ? times : parseCsv(settings.pickupTimesWeekday);
  }

  // Times offered for a whole selection. Pickup with a chosen date is the only
  // combination that has any — home delivery never asks for a time, and without
  // a date there is no weekday to look up.
  function pickupTimesForSelection(method, iso, settings) {
    if (method !== 'pickup' || !iso) return [];
    return pickupTimesFor(new Date(iso + 'T00:00:00'), settings);
  }

  // The whole pickup-time policy for a selection, in one place:
  //   times    — what the dropdown offers
  //   visible  — pickup shows the field, except when the chosen date's day has
  //              no configured times (hiding beats a dead disabled select)
  //   required — checkout must wait for a choice exactly when times are offered
  function pickupTimeState(method, iso, settings) {
    const times = pickupTimesForSelection(method, iso, settings);
    return {
      times,
      visible: method === 'pickup' && !(iso && times.length === 0),
      required: times.length > 0,
    };
  }

  // Earliest/latest selectable dates from lead days + max weeks, relative to now.
  // A fixed date beyond the normal window extends `latest` so it can be reached.
  function dateRange(settings, now) {
    const base = startOfDay(now || new Date());
    const leadDays = parseInt(settings.leadDays != null ? settings.leadDays : 1, 10);
    const maxWeeks = parseInt(settings.maxWeeks != null ? settings.maxWeeks : 4, 10);
    const earliest = new Date(base);
    earliest.setDate(earliest.getDate() + leadDays);
    let latest = new Date(base);
    latest.setDate(latest.getDate() + maxWeeks * 7);
    const fixed = fixedDateOf(settings);
    if (fixed) {
      const f = new Date(fixed + 'T00:00:00');
      if (f > latest) latest = f;
    }
    return { earliest, latest };
  }

  // Whether a specific date can be chosen for the given method.
  function isSelectable(date, method, settings, range) {
    const r = range || dateRange(settings);
    const fixed = fixedDateOf(settings);
    if (fixed) return isoDate(date) === fixed && date >= r.earliest;
    const days = deliveryDaysFor(method, settings);
    const blocked = parseCsv(settings.blockedDates);
    return (
      days.includes(date.getDay()) &&
      !blocked.includes(isoDate(date)) &&
      date >= r.earliest &&
      date <= r.latest
    );
  }

  // First day of the month that contains the earliest selectable date, so the
  // calendar opens on a useful month. Falls back to today if none within range.
  function firstSelectableMonth(method, settings, now) {
    const fixed = fixedDateOf(settings);
    if (fixed) {
      const f = new Date(fixed + 'T00:00:00');
      return new Date(f.getFullYear(), f.getMonth(), 1);
    }
    const range = dateRange(settings, now);
    const d = new Date(range.earliest);
    for (let i = 0; i < 90; i++) {
      if (isSelectable(d, method, settings, range)) {
        return new Date(d.getFullYear(), d.getMonth(), 1);
      }
      d.setDate(d.getDate() + 1);
    }
    return startOfDay(now || new Date());
  }

  // The packaging item belongs in the cart for home delivery (national or
  // local) when the cart also holds at least one other item.
  function shouldHavePackaging(method, hasOtherItems) {
    return (method === 'shipping' || method === 'local') && !!hasOtherItems;
  }

  return {
    DEFAULT_DAYS,
    startOfDay,
    isoDate,
    deliveryDaysFor,
    methodLabelFor,
    isLocalPostcode,
    parseCsv,
    pickupTimesFor,
    pickupTimesForSelection,
    pickupTimeState,
    dateRange,
    isSelectable,
    firstSelectableMonth,
    shouldHavePackaging,
  };
});
